import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Chat from '@botsquad/web-client'

import { conversationTitle, conversationAvatar } from '../common/conversations'
import { localePreflist, resolveTranslations } from '../common/labels'

import AppState from './state'
import NavBar from './NavBar'
import { MAPS_API_KEY } from '../utils'

import {
  getUserInfo,
  mergeDeep,
  isMobile,
  getChannelParams,
  getQueryParams,
  consumeChannelParams,
} from '../common/util'
import { getExternalSocket, getConfig } from './util'

const READ_TIMEOUT = 10000

const ChatScreen = props => {
  const [connected, setConnected] = useState(false)
  const [hideInput, setHideInput] = useState(false)
  const [conversationData, setConversationData] = useState({})

  const navigate = useNavigate()
  const socketRef = useRef(null)
  const channelRef = useRef(null)
  const readTimerRef = useRef(null)
  const paramsRef = useRef(null)

  useEffect(() => {
    socketRef.current = getExternalSocket(connected => setConnected(connected))
    buildParams()

    return () => {
      delete window.ChatScreen

      if (readTimerRef.current) {
        clearTimeout(readTimerRef.current)
        readTimerRef.current = null
      }
      if (channelRef.current) {
        channelRef.current.leave()
        channelRef.current = null
        if (typeof props.onLeave === 'function') {
          props.onLeave()
        }
      }
    }
  }, [])

  const buildParams = () => {
    const user = getUserInfo()
    const params = getChannelParams()
    let query = getQueryParams()
    const connect_payload = { origin: document.location.href }
    let context = JSON.parse(getConfig().botContext || '{}')
    context = mergeDeep({ user, query }, context)

    const { button } = props
    const g = button && button.g
    paramsRef.current = { ...params, connect_payload, context, g }
  }

  const tr = value => {
    return resolveTranslations(value, props.locale, props.bot)
  }

  const setChannel = ch => {
    consumeChannelParams() // clean up stateful URL query parameters
    channelRef.current = ch
    channelRef.current.on('conversation_meta', conversation => {
      const { conversation_data } = conversation
      setConversationData(conversation_data)

      if (readTimerRef.current) {
        clearTimeout(readTimerRef.current)
      }
      if (conversation.unread_message_count > 0) {
        readTimerRef.current = setTimeout(() => {
          channelRef.current.push('set_conversation_read', {})
        }, READ_TIMEOUT)
      }
    })
    if (props.button && props.button.event) {
      const { event } = props.button
      channelRef.current.push('user_action', { type: 'event', payload: event })
    }
    if (typeof props.onJoin === 'function') {
      props.onJoin(ch)
    }
  }

  const onEmit = ({ event, payload }) => {
    if (event === 'pwa_intro_done') {
      AppState.setIntroDone(true)
      navigate('/', { replace: true })
    }
    if (event === 'pwa_menu') {
      navigate('/')
    }
    if (event === 'pwa_navigate') {
      navigate(payload)
    }
    if (event === 'close_conversation') {
      closeConversation()
    }
    if (event === 'hide_input') {
      setHideInput(payload)
    }
    if (typeof props.onEmit === 'function') {
      props.onEmit(event, payload)
    }
  }

  const renderTitle = () => {
    const { button, operators } = props

    return (
      <>
        {conversationAvatar(conversationData, null, operators)}
        {conversationTitle(conversationData, props.bot, button?.title ? tr(button.title) : null)}
      </>
    )
  }

  const hasOtherUnread = () => {
    const { conversations } = props.conversationManager.component.state
    const { g } = props.button
    const c = conversations.filter(c => c.g !== g).reduce((sum, c) => sum + c.unread_message_count, 0)
    return c > 0
  }

  const closeConversation = async () => {
    const { g } = props.button

    await props.conversationManager.closeConversation(g || 'main')

    if (AppState.showBackButton()) {
      navigate(-1)
    } else {
      document.location.reload()
    }
  }

  const { button } = props
  const { pwa } = props.bot

  const hide_input = hideInput || button?.hide_input
  const chat_config = (AppState.isEmbedded() ? pwa.embed_chat_config : button?.chat_config || pwa.chat_config) || {
    disabled_inputs: [],
  }
  const ui_labels = props.bot.ui_labels || {}

  return (
    <div className="chat-screen--wrapper botsquad-components layout-docked">
      <NavBar className="navbar" title={renderTitle()} hasUnread={hasOtherUnread()} />
      {socketRef.current && (
        <Chat
          bot_id={props.bot.id}
          mapsApiKey={MAPS_API_KEY}
          socket={socketRef.current}
          notificationManager={true}
          online={props.online && connected}
          hideHeader
          closeConversation={closeConversation}
          botAvatar={props.bot.profile_picture}
          onEmit={onEmit}
          onChannel={(ch, _joinParam, api) => {
            setChannel(ch)
            window.ChatScreen = api
          }}
          onHideInput={hide_input => setHideInput(hide_input)}
          localePrefs={localePreflist(props.locale, props.bot)}
          settings={{ layout: 'docked', alwaysFocus: !isMobile(), hide_input, chat_config, ui_labels }}
          params={paramsRef.current}
        />
      )}
    </div>
  )
}

export default ChatScreen
